import { makeAutoObservable } from "mobx";
import ApiStore from "./ApiStore";
import { Dayjs } from 'dayjs';
import { slugify } from "../routes/RouterUtilities";
import { Skill } from "../models/Skill";
import { Employee } from "../models/Employee";

export type NewProject = {
  title: string,
  description: string,
  startDate: Dayjs,
  isActive: boolean,
  isPublish: boolean,
}

export type AssignUserProject = {
  id?: string,
  userId: string,
  projectId: string,
  roleId: string,
}

export type UserProject = {
  id: string,
  firstName: string,
  lastName: string,
  email: string,
  photoId: string,
  role: {
    id: string,
    title: string
  }
}

export interface ProjectSkill {
  skillId: string;
  projectId: number;
}

export type Project = {
  id: string,
  title: string,
  description: string,
  startDate: Dayjs,
  isActive: boolean,
  isPublish: boolean,
  companyId: number,
  projectResponsible: Employee,
  userProjects: UserProject[],
  members: UserProject[],
  developers: UserProject[],
  projectSkills: Skill[],
  [x: string]: any
}

export interface ProjectAccess {
  id: string;
  createdAt: string;
  endDate: string;
  jwtToken: string;
  authorId: string;
}

class ProjectsStore {
  isLoading: boolean = true;
  ApiStore!: ApiStore;
  project!: Project;
  publicAccess!: ProjectAccess[];
  projects!: Project[];

  constructor(apiStore: ApiStore) {
    makeAutoObservable(this);
    this.ApiStore = apiStore;
    this.projects = [];
    this.publicAccess = [];

    if (typeof window !== "undefined") {
      window.addEventListener("app:logout", this.reset);
      window.addEventListener("company:changed", this.reset);
    }
  }

  //#region Reset methods
  reset = () => {
    this.projects = [];
    this.project = {} as any;
    this.publicAccess = [];
    this.isLoading = false;
  };
  //#endregion

  
  //#region Setters
  setLoading = (loading: boolean) => {
    this.isLoading = loading;
  }
  //#endregion


  //#region Projects Management
  getAll = async (forced: boolean = false) => {
    let projects: void | Project[] = this.projects;
    if (projects && projects.length > 0 && !forced) {
      return projects;
    } else {
      this.isLoading = true;
      projects = await this.ApiStore.ProjectsApiClient.getProjects().catch(() => {
        this.isLoading = false;
      });
      this.isLoading = false;
      if (projects) {
        this.projects = projects;
      }
    }
    return projects;
  }

  getById = async (id: string, forceLoad: boolean = false) => {
    let project = await this.projects.find(el => el.id == id);
    if (project && !forceLoad) {
      this.project = project;
    } else {
      this.isLoading = true;
      project = await this.ApiStore.ProjectsApiClient.getProjectsById(id);
      this.project = project;
      this.isLoading = false;
    }
    return project;
  }

  async getBySlug(slug: string, forceLoad: boolean = false): Promise<Project | undefined> {
    try {
      if (!isNaN(Number(slug))) {
        return await this.getById(slug, forceLoad);
      }

      if (this.projects && this.projects.length > 0) {
        const project = this.projects.find(p => slugify(p.title || p.name) === slug);
        if (project) {
          return await this.getById(project.id.toString(), forceLoad);
        }
      }

      await this.getAll();

      const project = this.projects.find(p => slugify(p.title || p.name) === slug);
      if (project) {
        return await this.getById(project.id.toString(), forceLoad);
      }

      return undefined;
    } catch (error) {
      throw error;
    }
  }

  getProjectSlug(projectId: string): string | undefined {
    const project = this.projects.find(p => p.id.toString() === projectId);
    return project ? slugify(project.title || project.name) : undefined;
  }

  createNew = async (project: NewProject) => {
    this.isLoading = true;

    const response = await this.ApiStore.ProjectsApiClient.addProject(project);
    this.isLoading = false;

    return response;
  }

  updateCurrentProject = async () => {
    const project = await this.ApiStore.ProjectsApiClient.getProjectsById(this.project.id);
    if (project) {
      this.project = project;
    }
    return project;
  }

  editProject = async (newProject: Project) => {
    this.isLoading = true;
    const result = await this.ApiStore.ProjectsApiClient.editProjects(newProject);
    this.isLoading = false;
    if (result) {
      this.project = result;
    }
    return result;
  }

  deleteById = async (id: string) => {
    this.isLoading = true;
    const project = await this.ApiStore.ProjectsApiClient.deleteProjectsById(id).then(() => {
      this.projects = this.projects.filter(p => p.id != id)
    }).finally(() => {
      this.isLoading = false;
    });
    return project;
  }
  //#endregion


  //#region User Assignment
  AssignNewUser = async (project: NewProject) => {
    this.isLoading = true;

    const response = await this.ApiStore.ProjectsApiClient.addProject(project);
    this.isLoading = false;

    return response;
  }

  public assignNewUser = async (item: AssignUserProject): Promise<any> => {
    this.isLoading = true;
    const project = await this.ApiStore.ProjectsApiClient.assignNewUser(item).finally(async () => {
      await this.updateCurrentProject();
      this.isLoading = false;
    });

    return project;
  }

  public editAssignUser = async (item: AssignUserProject): Promise<any> => {
    const project = await this.ApiStore.ProjectsApiClient.editAssignUser(item).finally(async () => {
      await this.updateCurrentProject();
      this.isLoading = false;
    });

    return project;
  }

  public deleteAssignUser = async (userId: string, projectId: string): Promise<any> => {
    const project = await this.ApiStore.ProjectsApiClient.deleteAssignUser(userId, projectId).finally(async () => {
      await this.updateCurrentProject();
      this.isLoading = false;
    });
    return project;
  }
  //#endregion


  //#region Public Access Management
  addAccess = async (endDate: Dayjs) => {
    this.isLoading = true;
    const response = await this.ApiStore.ProjectsApiClient.addPublicAccess(this.project.id.toString(), endDate);
    this.isLoading = false;
    return response;
  }

  getAccesses = async (): Promise<ProjectAccess[] | false> => {
    this.isLoading = true;
    const response = await this.ApiStore.ProjectsApiClient.getPublicAccesses(this.project.id.toString());
    this.isLoading = false;

    if (response) {
      this.publicAccess = response;
      return response;
    }

    return false;
  }

  removeAccess = async (index: string) => {
    this.isLoading = true;
    const response = await this.ApiStore.ProjectsApiClient.removePublicAccess(this.project.id.toString(), index);
    this.isLoading = false;
    return response;
  }
  //#endregion
}
export default ProjectsStore;
